import React, { useState, useEffect } from 'react'
import { Row, Col, Typography, Button, Space } from 'antd'
import { CalendarOutlined, VideoCameraOutlined, SunOutlined, StarOutlined, CloudOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { handleClinicBooking, handleVirtualConsult } from '../../../../../Bookings/utils/lib'
import NextAppointmentCard from './NextAppointmentCard'
import ProfileHistoryCard from './ProfileHistoryCard'
import DownloadAppCard from './DownloadAppCard'
import QuickLinksCard from './QuickLinksCard'

const { Title } = Typography

const PatientDashboard = () => {
  const dispatch = useDispatch()
  const entityState = useSelector((state) => state.core.entity)
  const authState = useSelector((state) => state.auth)
  const [isVisible, setIsVisible] = useState(false)

  // Get user's first name
  const getUserName = () => {
    const user = authState.agiliteUser
    if (user?.firstName) {
      return user.firstName
    }
    if (user?.name) {
      return user.name.split(' ')[0]
    }
    return 'there'
  }

  // Dynamic greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) {
      return {
        text: 'Good morning',
        icon: <SunOutlined />,
        color: '#faad14',
        bgGradient: 'linear-gradient(135deg, #fff7e6 0%, #fff1b8 100%)'
      }
    } else if (hour < 17) {
      return {
        text: 'Good afternoon',
        icon: <SunOutlined />,
        color: '#fa8c16',
        bgGradient: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)'
      }
    } else if (hour < 21) {
      return {
        text: 'Good evening',
        icon: <StarOutlined />,
        color: '#722ed1',
        bgGradient: 'linear-gradient(135deg, #f9f0ff 0%, #d3adf7 100%)'
      }
    } else {
      return {
        text: 'Good night',
        icon: <CloudOutlined />,
        color: '#13c2c2',
        bgGradient: 'linear-gradient(135deg, #e6fffb 0%, #87e8de 100%)'
      }
    }
  }

  // Animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const greeting = getGreeting()
  const userName = getUserName()

  return (
    <>
      {/* CSS Keyframes for animations */}
      <style>
        {`
          @keyframes shimmer {
            0% { left: -100%; }
            50% { left: 100%; }
            100% { left: 100%; }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-4px); }
            60% { transform: translateY(-2px); }
          }
        `}
      </style>

      <div
        style={{
          padding: '24px 48px 48px',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          minHeight: '100vh'
        }}
      >
        {/* Header with Greeting and Quick Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}
        >
          {/* Dynamic Greeting with Animation */}
          <div
            style={{
              background: greeting.bgGradient,
              borderRadius: '16px',
              padding: '20px 32px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.8)',
              transform: isVisible ? 'translateY(0px)' : 'translateY(-20px)',
              opacity: isVisible ? 1 : 0,
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle shimmer effect */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: isVisible ? 'shimmer 3s ease-in-out infinite' : 'none'
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  fontSize: '36px',
                  color: greeting.color,
                  animation: isVisible ? 'bounce 2s ease-in-out infinite' : 'none'
                }}
              >
                {greeting.icon}
              </div>
              <Title
                level={2}
                style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '500',
                  color: '#262626'
                }}
              >
                {greeting.text},{' '}
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    color: greeting.color,
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    background: `linear-gradient(135deg, ${greeting.color}, ${greeting.color}dd)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    // Fallback for browsers that don't support gradient text
                    '@supports not (-webkit-background-clip: text)': {
                      WebkitTextFillColor: 'inherit',
                      color: greeting.color
                    }
                  }}
                >
                  {userName}
                </span>
              </Title>
            </div>
          </div>
          <Space size='middle'>
            <Button
              type='primary'
              icon={<CalendarOutlined />}
              size='large'
              onClick={() => handleClinicBooking(false, dispatch)}
            >
              Schedule In-Person Visit
            </Button>
            {entityState.virtualConsultations.enabled && (
              <Button
                type='primary'
                icon={<VideoCameraOutlined />}
                size='large'
                onClick={() => handleVirtualConsult(false, dispatch)}
              >
                Schedule Virtual Consult
              </Button>
            )}
          </Space>
        </div>

        {/* Main Content Grid */}
        <Row gutter={[24, 24]}>
          {/* Top Row - Primary Cards */}
          <Col xs={24} md={12} lg={8}>
            <NextAppointmentCard />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <ProfileHistoryCard />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <DownloadAppCard />
          </Col>

          {/* Bottom Row - Quick Actions spanning full width */}
          <Col xs={24}>
            <QuickLinksCard />
          </Col>
        </Row>
      </div>
    </>
  )
}

export default PatientDashboard
